// Backend serializes JSON property names as declared (PascalCase) because
// ~67 files / 242 read sites depend on names like `response.data.ApiObjectData`,
// `offer.Title`, `car.BrandID`, etc. Newer UI code (home page, etc.) reads
// camelCase. Instead of touching either side, this normalizer walks every
// API response and ADDS a camelCase alias for each PascalCase key. The
// original PascalCase property stays in place, so existing readers keep
// working; new camelCase readers now find the data too.
//
// Non-destructive: aliases only added when the camelCase key doesn't already
// exist on the object.

const isPlainContainer = (v) =>
    v !== null &&
    typeof v === "object" &&
    !(v instanceof Date) &&
    !(v instanceof RegExp) &&
    !(v instanceof Map) &&
    !(v instanceof Set) &&
    !(v instanceof ArrayBuffer);

function camelize(key) {
    // Only treat as PascalCase when the first char is an ASCII uppercase letter.
    const c0 = key.charCodeAt(0);
    if (c0 < 65 || c0 > 90) return null; // not A-Z
    return key[0].toLowerCase() + key.slice(1);
}

export function addCamelCaseAliases(value, seen = new WeakSet()) {
    if (!isPlainContainer(value)) return value;
    if (seen.has(value)) return value; // guard against cycles
    seen.add(value);

    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
            addCamelCaseAliases(value[i], seen);
        }
        return value;
    }

    // Snapshot keys first so aliases we add during iteration aren't revisited.
    const keys = Object.keys(value);
    for (const key of keys) {
        const v = value[key];
        const camel = camelize(key);
        if (camel && camel !== key && !(camel in value)) {
            try {
                value[camel] = v;
            } catch (_e) {
                // Frozen / read-only object — silently skip.
            }
        }
        if (isPlainContainer(v)) addCamelCaseAliases(v, seen);
    }
    return value;
}

// Wires the normalizer into an axios instance as a response interceptor.
// Idempotent: calling more than once on the same instance is safe because the
// normalizer's alias check (`!(camel in value)`) makes repeated runs no-ops.
export function installAliasInterceptor(axiosInstance) {
    if (!axiosInstance || !axiosInstance.interceptors) return;
    axiosInstance.interceptors.response.use((response) => {
        try {
            addCamelCaseAliases(response?.data);
        } catch (_e) {
            // Defensive: never block a real response on normalization failure.
        }
        return response;
    });
}
